/**
 * Offline indicator banner.
 *
 * Critical for a fire-information app: users in regional Australia on poor
 * mobile connections need explicit warning when they're seeing potentially
 * stale data. Without this, the map silently keeps showing the last known
 * fire state — and during a fire emergency that's the difference between
 * "current threat" and "30 minutes ago when it was 5km further away."
 *
 * The browser fires `online` and `offline` events on `window` when the
 * navigator.onLine state flips. Note: navigator.onLine is conservative —
 * it tells us when the device thinks it has no network at all, but a working
 * Wi-Fi connection without internet access still reports onLine=true.
 * That's a known limitation; covering it would require active probing
 * which adds noise. The current implementation handles the most common
 * regional-mobile scenario: connection drops → banner appears.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Format "X minutes/hours ago" for the offline-since timestamp.
 */
function formatOfflineDuration(offlineSinceMs: number, nowMs: number): string {
  const seconds = Math.max(0, Math.round((nowMs - offlineSinceMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `${hours} hr`;
}

export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  // Wall-clock millisecond when the user went offline. Used to display
  // "Offline for X min" so users know how stale the data they're looking
  // at might be. Critical safety signal during fire events.
  const [offlineSinceMs, setOfflineSinceMs] = useState<number | null>(
    typeof navigator !== 'undefined' && !navigator.onLine ? Date.now() : null
  );
  // Re-render every 30s while offline so the duration updates. Cleared
  // immediately when back online.
  const tickRef = useRef<number | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setOfflineSinceMs(null);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setOfflineSinceMs(Date.now());
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Periodic re-render while offline to keep the duration current.
  useEffect(() => {
    if (isOnline) {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    tickRef.current = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [isOnline]);

  if (isOnline) return null;

  const duration =
    offlineSinceMs !== null ? formatOfflineDuration(offlineSinceMs, Date.now()) : null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="offline-banner"
    >
      <span className="offline-banner-pulse" aria-hidden="true" />
      <svg
        className="offline-banner-icon"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
        <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </svg>
      <div className="offline-banner-text">
        <strong className="offline-banner-title">
          OFFLINE — fire data is not updating{duration ? ` (${duration})` : ''}
        </strong>
        <span className="offline-banner-sub">
          You're looking at the last data the app loaded. For live emergencies, follow
          your state's emergency service and call <strong>000</strong>.
        </span>
      </div>
    </div>
  );
}
